/**
 * Tracking Utilities
 * Fonctions globales pour le tracking Facebook et autres services
 * Ce fichier doit être chargé EN PREMIER dans theme.liquid
 */

/**
 * Récupère l'adresse IP du client via l'API ipify
 * @returns {Promise<string>} L'adresse IP du client ou une chaîne vide en cas d'erreur
 */
var fsClientIpPromise = null;
async function getClientIpAddress(timeoutMs) {
  // Mise en cache pour la session ; ne bloque jamais plus de timeoutMs (ipify bloqué ou lent)
  timeoutMs = timeoutMs || 4000;
  try {
    var cachedIp = sessionStorage.getItem('fs_client_ip');
    if (cachedIp) return cachedIp;
  } catch (e) {}

  if (!fsClientIpPromise) {
    fsClientIpPromise = (async function () {
      var controller = new AbortController();
      var timer = setTimeout(function () { controller.abort(); }, timeoutMs);
      try {
        const response = await fetch('https://api.ipify.org?format=json', { signal: controller.signal });
        const data = await response.json();
        if (data && data.ip) {
          try { sessionStorage.setItem('fs_client_ip', data.ip); } catch (e) {}
          return data.ip;
        }
        return '';
      } catch (error) {
        console.warn('[Tracking] IP client indisponible (ipify bloqué ou trop lent), envoi sans IP');
        return '';
      } finally {
        clearTimeout(timer);
      }
    })();
    // En cas d'échec, permettre un nouvel essai au prochain appel
    fsClientIpPromise.then(function (ip) { if (!ip) fsClientIpPromise = null; });
  }
  return fsClientIpPromise;
}

/**
 * Hash un email avec SHA-256 pour Facebook Pixel
 * @param {string} email - L'email à hasher
 * @returns {Promise<string|null>} Le hash SHA-256 de l'email ou null
 */
async function hashEmail(email) {
  if (!email) return null;
  
  try {
    const normalized = email.trim().toLowerCase();
    const msgBuffer = new TextEncoder().encode(normalized);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('Error hashing email:', error);
    return null;
  }
}

/**
 * Récupère les métadonnées des produits du panier
 * @returns {Promise<Object>} Les métadonnées du panier
 */
async function getProductMetaData() {
  try {
    const response = await fetch('/cart?view=meta-data.json');
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching product metadata:', error);
    return null;
  }
}


// ==========================================================================
// Tracking Facebook (Conversions API) + TikTok
// ==========================================================================

// Identifiant produit envoyé à Meta : 'metafield'
// ('metafield' = id CheckoutChamp du metafield productId, 'product_id' = id produit Shopify)
var FS_CONTENT_ID_SOURCE = 'metafield';

function fsGetCookie(name) {
  var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function fsSetCookie(name, value, days) {
  document.cookie = name + '=' + value + '; path=/; max-age=' + days * 86400 + '; SameSite=Lax';
}

// Identifiant navigateur Meta (_fbp), créé s'il n'existe pas
function getFbp() {
  var fbp = fsGetCookie('_fbp');
  if (!fbp) {
    fbp = 'fb.1.' + Date.now() + '.' + Math.floor(Math.random() * 1e10);
    fsSetCookie('_fbp', fbp, 90);
  }
  return fbp;
}

// Identifiant de clic Meta (_fbc), construit à partir du fbclid de l'URL de la pub
function getFbc() {
  var fbclid = new URLSearchParams(window.location.search).get('fbclid');
  var fbc = fsGetCookie('_fbc');
  if (fbclid && (!fbc || fbc.slice(-(fbclid.length + 1)) !== '.' + fbclid)) {
    fbc = 'fb.1.' + Date.now() + '.' + fbclid;
    fsSetCookie('_fbc', fbc, 90);
  }
  return fbc || null;
}

function generateEventId(prefix) {
  return prefix + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
}

async function sendFacebookEvent(eventName, customData, options) {
  options = options || {};
  if (!Array.isArray(window.pixelConfigs) || window.pixelConfigs.length === 0) {
    console.warn('pixelConfigs non défini ou vide');
    return;
  }

  var clientIP = await getClientIpAddress(1500);

  var userData = Object.assign({
    client_user_agent: navigator.userAgent,
    fbp: getFbp()
  }, options.userData || {});
  var fbc = getFbc();
  if (fbc) userData.fbc = fbc;
  if (clientIP) userData.client_ip_address = clientIP;

  var event = {
    event_name: eventName,
    event_time: Math.floor(Date.now() / 1000),
    event_id: options.eventId || generateEventId(eventName),
    event_source_url: window.location.origin + window.location.pathname,
    action_source: 'website',
    user_data: userData,
    custom_data: customData
  };

  window.pixelConfigs.forEach(function (config) {
    fetch('https://graph.facebook.com/v18.0/' + config.pixelId + '/events?access_token=' + config.accessToken, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: [event] })
    })
      .then(function (response) { return response.json(); })
      .then(function (data) { console.log('Facebook ' + eventName + ' Response:', data); })
      .catch(function (error) { console.error('Facebook ' + eventName + ' Error:', error); });
  });
}

// Pixels TikTok utilisables : le proxy exige un token hexadécimal de 40 caractères
function getValidTikTokPixels() {
  if (!Array.isArray(window.tiktokPixelConfigs)) return [];
  return window.tiktokPixelConfigs.filter(function (config) {
    var valid = !!config.pixelId && /^[a-f0-9]{40}$/.test(config.accessToken || '');
    if (!valid && !window.__fsTikTokWarned) {
      window.__fsTikTokWarned = true;
      console.warn('[Tracking] TikTok désactivé pour "' + (config.pixelName || config.pixelId) + '" : accessToken manquant ou invalide');
    }
    return valid;
  });
}

async function sendTikTokEvent(eventName, eventData) {
  var tiktokPixels = getValidTikTokPixels();
  if (tiktokPixels.length === 0) return;

  var clientIP = await getClientIpAddress(1500);

  tiktokPixels.forEach(function (config) {
    fetch('https://api.data.retroha.com/tiktok_event.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pixelId: config.pixelId,
        accessToken: config.accessToken,
        eventName: eventName,
        eventData: eventData,
        context: {
          user_agent: navigator.userAgent,
          ip: clientIP || undefined,
          page: {
            url: window.location.href,
            referrer: document.referrer
          }
        }
      })
    })
      .then(function (response) { return response.json(); })
      .then(function (data) { console.log('TikTok ' + eventName + ' Response:', data); })
      .catch(function (error) { console.error('TikTok ' + eventName + ' Error:', error); });
  });
}

// Quantités demandées par variante dans la requête /cart/add
// (la réponse Shopify renvoie la quantité totale de la ligne, pas la quantité ajoutée)
function getRequestedQuantities(body) {
  var quantities = {};
  try {
    var entries = [];
    if (body instanceof FormData || body instanceof URLSearchParams) {
      entries = Array.from(body.entries());
    } else if (typeof body === 'string') {
      var json = JSON.parse(body);
      var items = Array.isArray(json.items) ? json.items : [json];
      items.forEach(function (item) {
        if (item && item.id) quantities[item.id] = parseInt(item.quantity, 10) || 1;
      });
      return quantities;
    }

    var singleId = entries.find(function (entry) { return entry[0] === 'id'; });
    if (singleId) {
      var quantity = entries.find(function (entry) { return entry[0] === 'quantity'; });
      quantities[singleId[1]] = parseInt(quantity && quantity[1], 10) || 1;
    }
    // Format items[0][id] / items[0][quantity]
    var itemIds = {};
    var itemQuantities = {};
    entries.forEach(function (entry) {
      var match = entry[0].match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (match) (match[2] === 'id' ? itemIds : itemQuantities)[match[1]] = entry[1];
    });
    Object.keys(itemIds).forEach(function (index) {
      quantities[itemIds[index]] = parseInt(itemQuantities[index], 10) || 1;
    });
  } catch (e) {}
  return quantities;
}

// AddToCart : envoyé une fois l'ajout confirmé par Shopify, avec les produits réellement ajoutés
async function trackAddToCart(addedItems, requestedQuantities) {
  requestedQuantities = requestedQuantities || {};
  var subscriptionVariantId = String(57789778264451);
  var items = addedItems.filter(function (item) {
    return item && item.variant_id && String(item.variant_id) !== subscriptionVariantId;
  });
  if (items.length === 0) return;

  // Identifiants CheckoutChamp (metafield productId) si c'est la source utilisée par ce thème
  var metaByVariant = {};
  if (FS_CONTENT_ID_SOURCE === 'metafield') {
    try {
      var metaResponse = await fetch('/cart?view=meta-data.json');
      var productMetaData = await metaResponse.json();
      (productMetaData.meta_product_id_array || []).forEach(function (meta) {
        metaByVariant[meta.variant_id] = meta;
      });
    } catch (e) {
      console.warn('[Tracking] meta-data du panier indisponible', e);
    }
  }

  var productIDs = [];
  var contentNames = [];
  var contentCategories = [];
  var totalPrice = 0;
  var numItems = 0;

  items.forEach(function (item) {
    if (FS_CONTENT_ID_SOURCE === 'product_id') {
      if (item.product_id) productIDs.push(String(item.product_id));
    } else {
      var meta = metaByVariant[item.variant_id];
      if (meta && meta.id) productIDs.push(String(meta.id).split('.')[0]);
    }
    var quantity = requestedQuantities[item.variant_id] || requestedQuantities[item.id] || item.quantity || 1;
    var unitPrice = (item.final_price != null ? item.final_price : item.price) || 0;
    contentNames.push(item.product_title || '');
    if (item.product_type) contentCategories.push(item.product_type);
    totalPrice += (unitPrice * quantity) / 100;
    numItems += quantity;
  });

  var value = parseFloat(totalPrice.toFixed(2));
  var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';

  var customData = {
    currency: currency,
    value: value,
    content_ids: productIDs,
    content_type: 'product',
    content_name: contentNames.join(', '),
    num_items: numItems
  };
  if (contentCategories.length > 0) customData.content_category = contentCategories.join(', ');

  sendFacebookEvent('AddToCart', customData);
  sendTikTokEvent('AddToCart', { currency: currency, value: value, content_type: 'product' });
}

// Intercepte tous les ajouts au panier (bouton principal, sticky bar, quick-add, bundles…)
(function installAddToCartTracking() {
  if (window.__fsAddToCartTrackingInstalled) return;
  window.__fsAddToCartTrackingInstalled = true;

  var originalFetch = window.fetch;
  window.fetch = function (input, init) {
    var request = originalFetch.apply(this, arguments);
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || String(input);
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var headers = (init && init.headers) || {};
      var noTrack = headers instanceof Headers ? headers.has('X-FS-No-Track') : !!headers['X-FS-No-Track'];

      if (method === 'POST' && !noTrack && /\/cart\/add(\.js)?(\?|$)/.test(url)) {
        var requestedQuantities = getRequestedQuantities(init && init.body);
        request.then(function (response) {
          if (!response.ok) return;
          response.clone().json()
            .then(function (data) { trackAddToCart(Array.isArray(data.items) ? data.items : [data], requestedQuantities); })
            .catch(function (error) { console.warn('[Tracking] AddToCart non envoyé', error); });
        }).catch(function () {});
      }
    } catch (e) {
      console.warn('[Tracking] Erreur interception ajout panier', e);
    }
    return request;
  };
})();

// Pré-chargement : capture du fbclid dès l'arrivée et IP mise en cache pour les événements suivants
try { getFbp(); getFbc(); } catch (e) {}
getClientIpAddress(1500);
